import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasPermission } from '../../../utils/permissions';
import { formatDisplayDate } from '../../../utils/dateFormat';
import { referralAPI, lookupAPI } from '../../../services/api';

const STATUS_LABELS = {
  incomplete:      'Incomplete',
  waiting:         'Waiting',
  active:          'Active',
  closing_out:     'Closing Out',
  transferring_out:'Transferring Out',
  transferring_in: 'Transferring In',
  closed:          'Closed',
};

const STATUS_OPTIONS = [
  { value: 'incomplete',      label: 'Incomplete' },
  { value: 'waiting',         label: 'Waiting' },
  { value: 'active',          label: 'Active' },
  { value: 'closing_out',     label: 'Closing Out' },
  { value: 'transferring_out',label: 'Transferring Out' },
  { value: 'transferring_in', label: 'Transferring In' },
];

function ReferralStatusBadge({ status }) {
  return (
    <span className={`referral-status-badge referral-status-${status || 'active'}`}>
      {STATUS_LABELS[status] || status || 'Active'}
    </span>
  );
}

function CurrentReferralSection({ currentReferral, clientId, tenantPath, onRefreshReferral }) {
  const navigate = useNavigate();
  const canCreateReferral = hasPermission('referrals.create');
  const canCloseReferral = hasPermission('referrals.close');
  const canUpdateReferral = hasPermission('referrals.update');
  const p = tenantPath || ((path) => path);

  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeDate, setCloseDate] = useState('');
  const [terminationReasonId, setTerminationReasonId] = useState('');
  const [terminationReasons, setTerminationReasons] = useState([]);
  const [closing, setClosing] = useState(false);

  const [statusValue, setStatusValue] = useState(currentReferral?.referral_status || 'active');
  const [savingStatus, setSavingStatus] = useState(false);

  useEffect(() => {
    setStatusValue(currentReferral?.referral_status || 'active');
  }, [currentReferral?.referral_status]);

  useEffect(() => {
    if (showCloseModal && terminationReasons.length === 0) {
      lookupAPI.getTerminationReasons().then((r) => {
        setTerminationReasons(r.data?.data || r.data || []);
      }).catch(() => setTerminationReasons([]));
    }
  }, [showCloseModal]);

  const formatDate = (dateString) => (dateString ? formatDisplayDate(dateString) : 'N/A');

  const isClosed = currentReferral && currentReferral.close_date;

  const handleOpenCloseModal = () => {
    setCloseDate(new Date().toISOString().split('T')[0]);
    setTerminationReasonId('');
    setShowCloseModal(true);
  };

  const handleCloseReferral = async () => {
    if (!closeDate || !terminationReasonId) {
      alert('Close date and termination reason are required.');
      return;
    }
    try {
      setClosing(true);
      await referralAPI.close(currentReferral.id, {
        close_date: closeDate,
        termination_reason_id: parseInt(terminationReasonId, 10),
      });
      setShowCloseModal(false);
      onRefreshReferral?.();
    } catch (err) {
      console.error('Error closing referral:', err);
      alert(err.response?.data?.error || 'Failed to close referral');
    } finally {
      setClosing(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === statusValue) return;
    try {
      setSavingStatus(true);
      await referralAPI.patchStatus(currentReferral.id, newStatus);
      setStatusValue(newStatus);
      onRefreshReferral?.();
    } catch (err) {
      console.error('Error updating referral status:', err);
      alert(err.response?.data?.error || 'Failed to update referral status');
      setStatusValue(currentReferral?.referral_status || 'active');
    } finally {
      setSavingStatus(false);
    }
  };

  if (!currentReferral) {
    return (
      <div className="form-section">
        <div className="section-title-row">
          <h3 className="section-title">Current Referral</h3>
          {canCreateReferral && (
            <button
              type="button"
              onClick={() => navigate(p(`/client/${clientId}/referral/new`))}
              className="btn-create-referral"
            >
              + Create Referral
            </button>
          )}
        </div>
        <div className="no-referral-message">
          <p>No referral history found for this client.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-section">
      <div className="section-title-row">
        <h3 className="section-title">Current Referral</h3>
        <div className="referral-actions">
          <button
            type="button"
            onClick={() => navigate(p(`/client/${clientId}/referrals`))}
            className="btn-view-all"
          >
            View All Referrals
          </button>
          <button
            type="button"
            onClick={() => navigate(p(`/client/${clientId}/referral/${currentReferral.id}`))}
            className="btn-view-referral"
          >
            View this referral
          </button>
          {!isClosed && canCloseReferral && (
            <button
              type="button"
              onClick={handleOpenCloseModal}
              className="btn-close-referral"
            >
              Close referral
            </button>
          )}
          {isClosed && canCreateReferral && (
            <button
              type="button"
              onClick={() => navigate(p(`/client/${clientId}/referral/new`))}
              className="btn-create-referral"
            >
              + Create New Referral
            </button>
          )}
        </div>
      </div>
      <div className="referral-status-display">
        <div className="form-grid">
          <div className="form-group">
            <label>Open Date</label>
            <div className="readonly-field">{formatDate(currentReferral.open_date)}</div>
          </div>
          <div className="form-group">
            <label>Close Date</label>
            <div className="readonly-field">
              {currentReferral.close_date ? formatDate(currentReferral.close_date) : 'Active'}
            </div>
          </div>

          <div className="form-group">
            <label>Referral Status</label>
            {!isClosed && canUpdateReferral ? (
              <select
                value={statusValue}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={savingStatus}
                className="referral-status-select"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <div className="readonly-field">
                <ReferralStatusBadge status={isClosed ? 'closed' : statusValue} />
              </div>
            )}
          </div>

          {currentReferral.close_date && currentReferral.termination_reason_description && (
            <div className="form-group form-group-full">
              <label>Termination Reason</label>
              <div className="readonly-field">
                {currentReferral.termination_reason_code} - {currentReferral.termination_reason_description}
              </div>
            </div>
          )}

          {currentReferral.referral_reason && (
            <div className="form-group form-group-full">
              <label>Referral reason</label>
              <div className="readonly-field">{currentReferral.referral_reason}</div>
            </div>
          )}
        </div>
      </div>

      {showCloseModal && (
        <div className="modal-overlay" onClick={() => !closing && setShowCloseModal(false)}>
          <div className="modal-content close-referral-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Close Referral</h3>
            <p className="modal-description">Enter the close date and termination reason. This updates the referral record.</p>
            <div className="form-group">
              <label htmlFor="close_date">Close Date <span className="required">*</span></label>
              <input
                type="date"
                id="close_date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="termination_reason_id">Termination Reason <span className="required">*</span></label>
              <select
                id="termination_reason_id"
                value={terminationReasonId}
                onChange={(e) => setTerminationReasonId(e.target.value)}
              >
                <option value="">Select a reason...</option>
                {terminationReasons.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code} - {r.description || r.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => !closing && setShowCloseModal(false)} className="btn-cancel">
                Cancel
              </button>
              <button type="button" onClick={handleCloseReferral} disabled={closing || !closeDate || !terminationReasonId} className="btn-save">
                {closing ? 'Closing...' : 'Close referral'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CurrentReferralSection;
